import React from "react";
import { Stethoscope, Brain, Grid3x3, FlaskConical, ClipboardList, Swords, Presentation } from "lucide-react";

const Games = ({ onViewChange, onBack }) => {
  const games = [
    {
      id: "live-session",
      title: "Live Guidelines",
      description: "Run a live teaching session — students join via QR code and answer questions on their phone",
      icon: Presentation,
      gradient: "from-indigo-100 via-purple-50 to-violet-100",
      borderColor: "border-indigo-500",
      iconColor: "text-indigo-700",
      bgColor: "bg-indigo-100",
      backgroundImage: null,
    },
    {
      id: "med-rpg",
      title: "MedQuest RPG",
      description: "Battle bacteria, viruses & parasites. Choose the right treatment to save your patient!",
      icon: Swords,
      gradient: "from-purple-100 via-indigo-50 to-violet-100",
      borderColor: "border-purple-400",
      iconColor: "text-purple-700",
      bgColor: "bg-purple-100",
      backgroundImage: "/avatar-Medquest.png",
    },
    {
      id: "guess-disease",
      title: "Symptom Detective",
      description: "Identify diseases based on symptoms and test results",
      icon: Stethoscope,
      gradient: "from-red-100 via-rose-50 to-pink-100",
      borderColor: "border-red-300",
      iconColor: "text-red-600",
      bgColor: "bg-red-100",
      backgroundImage: "/avatar-detective.png",
    },
    {
      id: "pubquiz",
      title: "Pub Quiz",
      description: "Challenge yourself with medical trivia questions",
      icon: Brain,
      gradient: "from-purple-100 via-violet-50 to-indigo-100",
      borderColor: "border-purple-300",
      iconColor: "text-purple-600",
      bgColor: "bg-purple-100",
      backgroundImage: "/avatar-pubquiz.png",
    },
    {
      id: "crossword",
      title: "Medical Crossword",
      description: "Solve medical terminology puzzles",
      icon: Brain,
      gradient: "from-amber-100 via-yellow-50 to-orange-100",
      borderColor: "border-amber-300",
      iconColor: "text-amber-600",
      bgColor: "bg-amber-100",
      backgroundImage: "/avatar-crossword.png",
    },
    {
      id: "match",
      title: "Medical Match",
      description: "Match medical terms with their pairs",
      icon: Grid3x3,
      gradient: "from-teal-100 via-cyan-50 to-sky-100",
      borderColor: "border-teal-300",
      iconColor: "text-teal-600",
      bgColor: "bg-teal-100",
      backgroundImage: "/avatar-cardgame.png",
    },
    {
      id: "lab-interpreter",
      title: "Lab Interpreter",
      description: "Interpret lab results and diagnose conditions",
      icon: FlaskConical,
      gradient: "from-green-100 via-emerald-50 to-teal-100",
      borderColor: "border-green-300",
      iconColor: "text-green-600",
      bgColor: "bg-green-100",
      backgroundImage: "/avatar-lab.png",
    },
    {
      id: "diagnose-patient",
      title: "Diagnose the Patient",
      description: "Build a complete diagnostic workup and make your diagnosis",
      icon: ClipboardList,
      gradient: "from-indigo-100 via-blue-50 to-cyan-100",
      borderColor: "border-indigo-300",
      iconColor: "text-indigo-600",
      bgColor: "bg-indigo-100",
      backgroundImage: "/avatar-diagnose.png",
    },
  ];

  return (
    <div
      className="min-h-screen"
      style={{
        backgroundImage: "url('/avatar-gamer.png')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
      }}
    >
      <div className="min-h-screen bg-gradient-to-br from-green-900/80 to-emerald-900/80">
        <div className="max-w-5xl mx-auto px-4 py-8">
          <button
            onClick={onBack}
            className="mb-4 text-white hover:text-gray-200 flex items-center gap-2"
          >
            ← Back to Dashboard
          </button>

      <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200 mb-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">
              Medical Games
            </h1>
            <p className="text-gray-600">
              Test and improve your medical knowledge through interactive games
            </p>
          </div>
          <div className="w-16 h-16">
            <img
              src="/avatar-gamer.png"
              alt="Games"
              className="w-full h-full object-contain"
            />
          </div>
        </div>
      </div>

      <h2 className="text-2xl font-bold text-white mb-6">Choose a Game</h2>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {games.map((game) => {
          const GameIcon = game.icon;
          return (
            <button
              key={game.id}
              onClick={() => onViewChange(game.id)}
              className={
                "rounded-xl p-6 shadow-sm border-2 text-left transition-all transform hover:shadow-xl hover:scale-105 hover:-translate-y-1 bg-gradient-to-br relative overflow-hidden " +
                game.gradient +
                " " +
                game.borderColor
              }
              style={
                game.backgroundImage
                  ? {
                      backgroundImage: `url('${game.backgroundImage}')`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                      backgroundBlendMode: "overlay",
                    }
                  : undefined
              }
            >
              {game.backgroundImage && (
                <div className={
                  "absolute inset-0 bg-gradient-to-br " +
                  (game.id === "guess-disease"
                    ? "from-red-100/60 via-rose-50/60 to-pink-100/60"
                    : game.id === "pubquiz"
                    ? "from-purple-100/60 via-violet-50/60 to-indigo-100/60"
                    : game.id === "crossword"
                    ? "from-amber-100/60 via-yellow-50/60 to-orange-100/60"
                    : game.id === "match"
                    ? "from-teal-100/60 via-cyan-50/60 to-sky-100/60"
                    : game.id === "lab-interpreter"
                    ? "from-green-100/60 via-emerald-50/60 to-teal-100/60"
                    : game.id === "diagnose-patient"
                    ? "from-indigo-100/60 via-blue-50/60 to-cyan-100/60"
                    : game.id === "med-rpg"
                    ? "from-purple-100/60 via-indigo-50/60 to-violet-100/60"
                    : "from-gray-100/60 via-gray-50/60 to-gray-100/60")
                }></div>
              )}

              <div className="relative z-10">
                <div className="flex justify-center mb-4">
                  <div
                    className={
                      "inline-block p-3 rounded-full " + game.bgColor
                    }
                  >
                    <GameIcon className={"w-8 h-8 " + game.iconColor} />
                  </div>
                </div>

                <h3 className="text-lg font-bold text-gray-800 mb-2 text-center">
                  {game.title}
                </h3>
                <p className="text-sm text-gray-600 text-center">
                  {game.description}
                </p>

                <div className="mt-4 text-center">
                  <span className="text-sm font-semibold text-teal-600">
                    Play Now →
                  </span>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <div className="mt-8 bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-yellow-300 rounded-xl p-6">
        <h3 className="text-lg font-bold text-gray-800 mb-2">
          🎮 Coming Soon!
        </h3>
        <p className="text-gray-700 text-sm">
          These games are currently under development. Check back soon to test
          your medical knowledge and have fun while learning!
        </p>
      </div>
        </div>
      </div>
    </div>
  );
};

export default Games;
